import type { ColumnDef } from "@tanstack/react-table";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { FiArrowRight } from "react-icons/fi";

interface ReactTableProps<T extends object> {
	data: T[];
	columns: ColumnDef<T>[];
	customBgClass?: string;
	whiteSpaceWrap?: boolean;
	tableClassName?: string;
	/** Optional class applied to each td (e.g. "align-top" for top-aligned cells) */
	cellClassName?: string;
	/** When provided, row click uses this instead of default project navigation */
	onRowClick?: (original: T) => void;
}

export const Table = <T extends object>({
	data,
	columns,
	customBgClass,
	whiteSpaceWrap,
	tableClassName,
	cellClassName,
	onRowClick,
}: ReactTableProps<T>) => {
	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
	});
	const router = useRouter();

	const scrollRef = useRef<HTMLDivElement>(null);
	const [showScrollHint, setShowScrollHint] = useState(false);

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;

		const checkScroll = () => {
			const canScroll = el.scrollWidth > el.clientWidth;
			const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 5;
			setShowScrollHint(canScroll && !atEnd);
		};

		el.addEventListener("scroll", checkScroll);
		window.addEventListener("resize", checkScroll);

		checkScroll();

		return () => {
			el.removeEventListener("scroll", checkScroll);
			window.removeEventListener("resize", checkScroll);
		};
	}, []);

	return (
		<div className="ocp-metadata-text flex flex-col">
			{/*we want to show a hint that user can scroll to the right to see more projects.*/}
			<p className="mt-4 flex h-[4px] items-center justify-end gap-2 text-right md:hidden">
				{showScrollHint && (
					<>
						Scroll <FiArrowRight />
					</>
				)}
			</p>
			<div className="inline-block min-w-full py-0">
				<div className="overflow-auto p-2 px-0" ref={scrollRef}>
					<table className={`min-w-full ${tableClassName} border-spacing-y-0 text-center `}>
						<thead>
							{table.getHeaderGroups().map((headerGroup) => (
								<tr key={headerGroup.id}>
									{headerGroup.headers.map((header) => (
										<th
											key={header.id}
											className="px-6 pt-2 text-left text-sm text-nightSky md:py-4"
											style={{ width: header.getSize() }}
										>
											{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
										</th>
									))}
								</tr>
							))}
						</thead>
						<tbody>
							{table.getRowModel().rows.map((row, index) => (
								<tr
									key={row.id}
									className="cursor-pointer border-[1px] border-gray-200 py-6 text-left shadow hover:shadow-md"
									onClick={() => {
										const original = table.getRowModel()?.rows[index]?.original;
										if (!original) return;
										if (onRowClick) {
											onRowClick(original);
										} else {
											const projectData = original as { id?: number; status?: string };
											const status = projectData?.status;
											if (projectData?.id) {
												if (status === "IN_VALIDATION") {
													router.push(`/registry/in-validation/${projectData.id}`);
												} else {
													router.push(`/registry/${projectData.id}`);
												}
											}
										}
									}}
								>
									{row.getVisibleCells().map((cell) => {
										return (
											<td
												className={`${
													whiteSpaceWrap ? "whitespace-normal" : "whitespace-nowrap"
												} border-[0.5px] border-x-0 border-b-gray-200 ${
													customBgClass ? customBgClass : "bg-white"
												} px-6 py-4 text-sm font-light text-nightSky ${cellClassName ?? ""}`}
												key={cell.id}
											>
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</td>
										);
									})}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
};
